import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token")}` });

/**
 * useDashboardLayout — order + hidden + sizes + presets, persisted server-side.
 *
 *   const { visibleOrder, sizes, hidden,
 *           setOrder, toggleHidden, setSize, resetToDefault,
 *           applyPreset, isDirty, ready }
 *     = useDashboardLayout("patient_home", ["plan","adherence","booking"]);
 *
 *   applyPreset("focus") — user-provided preset dictionaries live on the
 *   consuming page and are passed through as `{ order, hidden, sizes }`.
 */
export function useDashboardLayout(surface, defaultOrder) {
  const [order, setOrderState] = useState(defaultOrder);
  const [hidden, setHiddenState] = useState(() => new Set());
  const [sizes, setSizesState] = useState({});
  const [ready, setReady] = useState(false);
  const toastCooldown = useRef(0);
  const defaultKey = useMemo(() => defaultOrder.join("|"), [defaultOrder]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/dashboard-layout`, { headers: H() });
        if (cancelled) return;
        const layout = data?.layout || {};
        setOrderState(mergeOrder(layout[surface] || [], defaultOrder));
        setHiddenState(new Set((layout[`${surface}__hidden`] || []).filter((id) => defaultOrder.includes(id))));
        // sizes are stored as a delimited "id:size,id:size" list on the same
        // per-surface key so we don't need a backend schema change.
        const rawSizes = (layout[`${surface}__sizes`] || []).reduce((acc, tok) => {
          const [id, sz] = String(tok).split(":");
          if (id && sz && defaultOrder.includes(id)) acc[id] = sz;
          return acc;
        }, {});
        setSizesState(rawSizes);
      } catch {
        /* silent fallback */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, defaultKey]);

  const persist = useCallback(async (patch, options = {}) => {
    try {
      const { data } = await axios.get(`${API}/me/dashboard-layout`, { headers: H() });
      const layout = { ...(data?.layout || {}) };
      if (patch.order) layout[surface] = patch.order;
      if (patch.hidden) layout[`${surface}__hidden`] = [...patch.hidden];
      if (patch.sizes) layout[`${surface}__sizes`] = Object.entries(patch.sizes).map(([k, v]) => `${k}:${v}`);
      if (patch.clear) {
        delete layout[surface];
        delete layout[`${surface}__hidden`];
        delete layout[`${surface}__sizes`];
      }
      await axios.put(`${API}/me/dashboard-layout`, { layout }, { headers: H() });
      const now = Date.now();
      if (!options.silent && now - toastCooldown.current > 4000) {
        toastCooldown.current = now;
        toast.success("Layout saved — synced to your other devices");
      }
    } catch {
      /* offline / auth blip — optimistic state stays */
    }
  }, [surface]);

  const setOrder = useCallback((next) => {
    setOrderState(next);
    persist({ order: next });
  }, [persist]);

  const toggleHidden = useCallback((id) => {
    setHiddenState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persist({ hidden: next });
      return next;
    });
  }, [persist]);

  const setSize = useCallback((id, size) => {
    setSizesState((prev) => {
      const next = { ...prev, [id]: size };
      persist({ sizes: next });
      return next;
    });
  }, [persist]);

  const resetToDefault = useCallback(() => {
    setOrderState(defaultOrder);
    setHiddenState(new Set());
    setSizesState({});
    persist({ clear: true }, { silent: true });
    toast.success("Layout reset to default");
  }, [defaultOrder, persist]);

  const applyPreset = useCallback((preset) => {
    if (!preset) return;
    const nextOrder = mergeOrder(preset.order || [], defaultOrder);
    const nextHidden = new Set((preset.hidden || []).filter((id) => defaultOrder.includes(id)));
    const nextSizes = { ...(preset.sizes || {}) };
    setOrderState(nextOrder);
    setHiddenState(nextHidden);
    setSizesState(nextSizes);
    persist({ order: nextOrder, hidden: nextHidden, sizes: nextSizes }, { silent: true });
    toast.success(`Preset "${preset.name || "preset"}" applied`);
  }, [defaultOrder, persist]);

  const visibleOrder = useMemo(() => order.filter((id) => !hidden.has(id)), [order, hidden]);
  const isDirty = useMemo(() => {
    if (hidden.size > 0) return true;
    if (Object.keys(sizes).length > 0) return true;
    return order.join("|") !== defaultKey;
  }, [order, hidden, sizes, defaultKey]);

  return {
    order, visibleOrder, hidden, sizes,
    setOrder, toggleHidden, setSize,
    resetToDefault, applyPreset,
    ready, isDirty,
  };
}

function mergeOrder(saved, def) {
  const defSet = new Set(def);
  const kept = (saved || []).filter((id) => defSet.has(id));
  const missing = def.filter((id) => !kept.includes(id));
  return [...kept, ...missing];
}
